import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import userService from '../../../services/userService';

const LocationSection = ({ formData, onChange, validationErrors = {}, isLoading = false }) => {
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);

  // Fetch countries on component mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        setCountriesLoading(true);
        const response = await userService.getAvailableCountries();

        if (response.success) {
          setCountries(response.data);
        } else {
          console.error('Failed to fetch countries:', response.error);
          // Fallback to a minimal set of countries if API fails
          setCountries([
            { code: 'US', name: 'United States', flag: '🇺🇸' },
            { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
            { code: 'DE', name: 'Germany', flag: '🇩🇪' },
            { code: 'FR', name: 'France', flag: '🇫🇷' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching countries:', error);
        // Fallback countries
        setCountries([
          { code: 'US', name: 'United States', flag: '🇺🇸' },
          { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' }
        ]);
      } finally {
        setCountriesLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Toronto', label: 'Toronto' },
    { value: 'Europe/London', label: 'London' },
    { value: 'Europe/Paris', label: 'Paris' },
    { value: 'Europe/Berlin', label: 'Berlin' },
    { value: 'Europe/Madrid', label: 'Madrid' },
    { value: 'Europe/Rome', label: 'Rome' },
    { value: 'Europe/Istanbul', label: 'Istanbul' },
    { value: 'Asia/Tokyo', label: 'Tokyo' },
    { value: 'Asia/Seoul', label: 'Seoul' },
    { value: 'Asia/Singapore', label: 'Singapore' },
    { value: 'Asia/Dubai', label: 'Dubai' },
    { value: 'Australia/Sydney', label: 'Sydney' },
    { value: 'Pacific/Auckland', label: 'Auckland' },
    { value: 'UTC', label: 'UTC' }
  ];

  const renderFieldError = (fieldName) => {
    const error = validationErrors[fieldName];
    if (error) {
      return (
        <p className="text-sm text-error-600 mt-1">
          {Array.isArray(error) ? error[0] : error}
        </p>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-accent-800 mb-2">
          Location Settings
        </h2>
        <p className="text-accent-600">
          Manage your location and regional preferences for better gift suggestions
        </p>
      </div>

      <div className="space-y-6">
        {/* Country */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Country *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Globe" size={18} className="text-accent-400" />
            </div>
            <select
              value={formData?.country || ''}
              onChange={(e) => onChange('country', e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${validationErrors.country ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading || countriesLoading}
            >
              <option value="">
                {countriesLoading ? 'Loading countries...' : 'Select a country'}
              </option>
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.flag ? `${country.flag} ${country.name}` : country.name}
                </option>
              ))}
            </select>
          </div>
          {renderFieldError('country')}
        </div>

        {/* City */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            City
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="MapPin" size={18} className="text-accent-400" />
            </div>
            <Input
              type="text"
              value={formData?.city || ''}
              onChange={(e) => onChange('city', e.target.value)}
              placeholder="Enter your city"
              className={`w-full pl-10 ${validationErrors.city ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            />
          </div>
          {renderFieldError('city')}
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Timezone
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Icon name="Clock" size={18} className="text-accent-400" />
            </div>
            <select
              value={formData?.timezone || ''}
              onChange={(e) => onChange('timezone', e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border border-input rounded-md bg-background text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${validationErrors.timezone ? 'border-error-300 focus:border-error-500' : ''}`}
              disabled={isLoading}
            >
              <option value="">Select a timezone</option>
              {timezones.map((timezone) => (
                <option key={timezone.value} value={timezone.value}>
                  {timezone.label}
                </option>
              ))}
            </select>
          </div>
          {renderFieldError('timezone')}
          <p className="text-sm text-accent-500 mt-1">
            Used for scheduling deliveries and displaying local times.
          </p>
        </div>

        {/* Location Services */}
        <div className="bg-accent-50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={20} className="text-accent-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-accent-800 mb-1">
                Location-Based Features
              </h4>
              <p className="text-sm text-accent-600">
                Your location helps us suggest local gift shops, calculate accurate delivery times,
                and provide region-specific gift recommendations. We never share your exact location
                with third parties.
              </p>
            </div>
          </div>
        </div>

        {/* Current Location Display */}
        {(formData?.country || formData?.city) && (
          <div className="bg-primary-50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="MapPin" size={20} className="text-primary-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-primary-800 mb-1">
                  Current Location
                </h4>
                <p className="text-sm text-primary-700">
                  {formData?.city && `${formData.city}, `}
                  {formData?.country && countries.find(c => c.code === formData.country)?.name}
                </p>
                {formData?.timezone && (
                  <p className="text-sm text-primary-600 mt-1">
                    Timezone: {timezones.find(tz => tz.value === formData.timezone)?.label || formData.timezone}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationSection;